import { makeService } from '@upstart/shared-api-utils';
import { UpstartLendingStates } from '@upstart/harmony/lib/legos/financingTypes';
import { OkResponse, RequestBody } from 'util/api';
import { useTypedBackendClient } from './useTypedBackendClient';

export type ListRicStateSchemasResponse = OkResponse<
  '/v2/ric-state-schemas',
  'get'
>['data'];

export type RicStateSchemaSummary = ListRicStateSchemasResponse[number];

export type LendingStateCode = RicStateSchemaSummary['stateCode'];

export type GetRicStateSchemaResponse = OkResponse<
  '/v2/ric-state-schemas/{stateCode}',
  'get'
>['data'];

export type SaveRicStateSchemaResponse = OkResponse<
  '/v2/ric-state-schemas/{stateCode}',
  'put'
>['data'];

export type RicStateSchemaContent = RequestBody<
  '/v2/ric-state-schemas/{stateCode}',
  'put'
>['content'];

export type ListRicStateSchemaRevisionsResponse = OkResponse<
  '/v2/ric-state-schemas/{stateCode}/revisions',
  'get'
>['data'];

export type RicStateSchemaRevision =
  ListRicStateSchemaRevisionsResponse[number];

export const isLendingStateCode = (state: string): state is LendingStateCode =>
  Object.keys(UpstartLendingStates).includes(state);

export interface RicStateSchemaService {
  listStates(): Promise<ListRicStateSchemasResponse>;
  getSchemaForState(params: {
    stateCode: LendingStateCode;
  }): Promise<GetRicStateSchemaResponse>;
  listRevisionsForState(params: {
    stateCode: LendingStateCode;
  }): Promise<ListRicStateSchemaRevisionsResponse>;
  saveSchemaForState(params: {
    stateCode: LendingStateCode;
    content: RicStateSchemaContent;
  }): Promise<SaveRicStateSchemaResponse>;
}

const useRicStateSchemaFns = (): RicStateSchemaService => {
  const { backendClient } = useTypedBackendClient();

  const listStates: RicStateSchemaService['listStates'] = async () => {
    const { data, error } = await backendClient.GET('/v2/ric-state-schemas');
    if (error) {
      throw new Error(error.detail || error.title);
    }
    return data.data;
  };

  const getSchemaForState: RicStateSchemaService['getSchemaForState'] = async ({
    stateCode,
  }) => {
    const { data, error } = await backendClient.GET(
      '/v2/ric-state-schemas/{stateCode}',
      { params: { path: { stateCode } } },
    );
    if (error) {
      throw new Error(error.detail || error.title);
    }
    return data.data;
  };

  const listRevisionsForState: RicStateSchemaService['listRevisionsForState'] =
    async ({ stateCode }) => {
      const { data, error } = await backendClient.GET(
        '/v2/ric-state-schemas/{stateCode}/revisions',
        { params: { path: { stateCode } } },
      );
      if (error) {
        throw new Error(error.detail || error.title);
      }
      return data.data;
    };

  const saveSchemaForState: RicStateSchemaService['saveSchemaForState'] =
    async ({ stateCode, content }) => {
      const { data, error } = await backendClient.PUT(
        '/v2/ric-state-schemas/{stateCode}',
        { params: { path: { stateCode } }, body: { content } },
      );
      if (error) {
        throw new Error(error.detail || error.title);
      }
      return data.data;
    };

  return {
    listStates,
    getSchemaForState,
    listRevisionsForState,
    saveSchemaForState,
  };
};

const {
  OverridesProvider: RicStateSchemaServiceOverridesProvider,
  useService: useRicStateSchemaService,
} = makeService({
  name: 'RicStateSchema',
  useServiceFns: useRicStateSchemaFns,
});

export { RicStateSchemaServiceOverridesProvider, useRicStateSchemaService };
